// Copyright (c) Microsoft. All rights reserved.

import { AlertDialogs } from '../../../libs/models/AlertDialogs';
import { AlertType } from '../../../libs/models/AlertType';
import { IArchiveSourceAvailability } from '../../../libs/models/ArchiveSourceAvailability';
import { TokenUsage } from '../../../libs/models/TokenUsage';

export interface ActiveUserInfo {
    id: string;
    email: string;
    username: string;
}

export enum AlertMetadata {
    NoArchiveSourcesSelected,
}

export interface Alert {
    message: string;
    type?: AlertType;
    showDialog?: boolean;
    hideDismissButton?: boolean;
    alertDialog?: AlertDialogs;
    metadata?: AlertMetadata;
}

interface Feature {
    enabled: boolean; // Whether to show the feature in the UX
    label: string;
    inactive?: boolean; // Set to true if you don't want the user to control the visibility of this feature or there's no backend support
    description?: string;
}

export interface Setting {
    title: string;
    description?: string;
    features: FeatureKeys[];
    stackVertically?: boolean;
    learnMoreLink?: string;
}

export enum IConversationDisplayMode {
    Active,
    Hidden,
    All,
}

export interface AppState {
    alerts: Alert[];
    activeUserInfo?: ActiveUserInfo;
    tokenUsage: TokenUsage;
    features: Record<FeatureKeys, Feature>;
    settings: Setting[];
    conversationDisplayMode: IConversationDisplayMode;
    archiveSourceAvailability?: IArchiveSourceAvailability;
    appErrorState?: boolean;
    isInputDisabled?: boolean;
}

export enum FeatureKeys {
    DarkMode,
    SimplifiedExperience,
    PluginsPlannersAndPersonas,
    AzureContentSafety,
    AzureCognitiveSearch,
    BotAsDocs,
    MultiUserChat,
    DeleteChats,
    RLHF, // Reinforcement Learning from Human Feedback
    ShowPrompt,
    ShowEffectivePromptsLink,
    HiddenChats,
}

export const Features = {
    [FeatureKeys.DarkMode]: {
        enabled: false,
        label: 'Dark Mode',
    },
    [FeatureKeys.SimplifiedExperience]: {
        enabled: true,
        label: 'Simplified Chat Experience',
    },
    [FeatureKeys.PluginsPlannersAndPersonas]: {
        enabled: false,
        label: 'Plugins & Planners & Personas',
        description: 'The Plans and Persona tabs are hidden until you turn this on',
    },
    [FeatureKeys.AzureContentSafety]: {
        enabled: false,
        label: 'Azure Content Safety',
        inactive: true,
    },
    [FeatureKeys.AzureCognitiveSearch]: {
        enabled: false,
        label: 'Azure Cognitive Search',
        inactive: true,
    },
    [FeatureKeys.BotAsDocs]: {
        enabled: false,
        label: 'Save/Load Chat Sessions',
    },
    [FeatureKeys.MultiUserChat]: {
        enabled: false,
        label: 'Live Chat Session Sharing',
    },
    [FeatureKeys.RLHF]: {
        enabled: false,
        label: 'Reinforcement Learning from Human Feedback',
        description: 'Enable users to vote on model-generated responses. For demonstration purposes only.',
        // TODO: [Issue #42] Send and store feedback in backend
        inactive: true,
    },
    [FeatureKeys.DeleteChats]: {
        enabled: false,
        label: 'Delete Chat Sessions',
        // TODO: [sk Issue #1642] Implement delete chats
        inactive: true,
    },
    [FeatureKeys.ShowPrompt]: {
        enabled: true,
        label: 'Show Prompt',
    },
    [FeatureKeys.ShowEffectivePromptsLink]: {
        enabled: true,
        label: 'Show Effective Prompts',
    },
    [FeatureKeys.HiddenChats]: {
        enabled: process.env.REACT_APP_FEATURE_HIDDEN_CHATS === 'Enabled',
        label: 'Enable Hidden Chats feature',
    },
};

export const Settings = [
    {
        // Basic settings has to stay at the first index. Add all new settings to end of array.
        title: 'Basic',
        features: [FeatureKeys.DarkMode, FeatureKeys.PluginsPlannersAndPersonas],
        stackVertically: true,
    },
    {
        title: 'Display',
        features: [FeatureKeys.SimplifiedExperience],
        stackVertically: true,
    },
    {
        title: 'Azure AI',
        features: [FeatureKeys.AzureContentSafety, FeatureKeys.AzureCognitiveSearch],
        stackVertically: true,
    },
    {
        title: 'Experimental',
        description: 'The related icons and menu options are hidden until you turn this on',
        features: [FeatureKeys.BotAsDocs, FeatureKeys.MultiUserChat, FeatureKeys.DeleteChats, FeatureKeys.RLHF],
    },
];

export const initialState: AppState = {
    alerts: [],
    tokenUsage: {},
    features: Features,
    settings: Settings,
    conversationDisplayMode: IConversationDisplayMode.Active,
    archiveSourceAvailability: { loaded: false, Sources: [] },
};
